'use client'

import React from 'react'
import { cn } from '@kiao-voice/ui-kit'
import { ANALYTICS_EVENTS } from '@/lib/analytics'
import { trackSiteEvent } from '@/lib/analytics/site-event'

/**
 * 常見問題。用原生 `<details>`：答案永遠留在 DOM 裡，收合狀態也進得了索引，
 * 而且不需要任何 JS。
 *
 * 一題可以有多段；段與段之間 10px，題與題之間 24px——段間距小於題間距，
 * 才看得出兩段屬於同一題。
 */

export interface FaqItem {
  /** The entry's fixed key (its i18n key, e.g. `q3`), reported as `question` on `faq_opened`. Never the text. */
  key: string
  question: string
  /** 一段一個節點。可以帶連結。 */
  answers: React.ReactNode[]
}

export interface FaqProps {
  items: FaqItem[]
  className?: string
}

export function Faq({ items, className }: FaqProps) {
  return (
    <div className={className}>
      {items.map((item, index) => (
        <details
          key={item.key}
          onToggle={(event) => {
            if (event.currentTarget.open) {
              trackSiteEvent(ANALYTICS_EVENTS.faqOpened, { question: item.key })
            }
          }}
          className={cn('group border-t', index === 0 ? 'border-foreground' : 'border-border')}
        >
          <summary className="relative cursor-pointer list-none py-5 pr-11 text-lg-plus leading-[1.5] font-semibold transition-opacity hover:opacity-72 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground [&::-webkit-details-marker]:hidden">
            {item.question}
            <span
              aria-hidden="true"
              className="absolute top-1/2 right-1.5 -mt-[7px] size-[11px] rotate-45 border-r-[1.5px] border-b-[1.5px] border-foreground transition-transform duration-200 group-open:-mt-[3px] group-open:rotate-225"
            />
          </summary>
          {item.answers.map((answer, answerIndex) => (
            <p
              key={answerIndex}
              className={cn(
                'text-md-plus leading-[1.8] text-muted',
                answerIndex === item.answers.length - 1 ? 'mb-6' : 'mb-2.5'
              )}
            >
              {answer}
            </p>
          ))}
        </details>
      ))}
    </div>
  )
}
